import React from 'react';
import {
    Dialog, 
    DialogContent,
    DialogTitle,
    TextField,
    DialogActions,
    Button,
    Alert,
    CircularProgress,
} from '@mui/material';
import { useState } from 'react';
import axios from 'axios';
import { resolveApiUrl } from '../utils/api';

interface GenerateProps{
    workzone: any[];
    open: boolean;
    onclose: () => void;
    onSignsGenerated: (signs: any[]) => void;
}

const initial_workdetails = {
    name: '',
    email: ''
};

export const Generate: React.FC<GenerateProps> = ({ 
    workzone, 
    open, 
    onclose,
    onSignsGenerated 
}) => {
    const [workdetails, setworkdetails] = useState(initial_workdetails);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    async function handleSubmit(workdetails: any) {
        if (!workzone || workzone.length === 0) {
            setError('Please ensure a workzone is selected');
            return;
        }

        setIsLoading(true);
        setError(null);
        try {            
            // Call the generate-tgs endpoint - backend does all processing
            const response = await axios.post(
                resolveApiUrl('/api/generate-tgs-preview'),
                {
                    workzone: workzone,
                    work_details: workdetails,
                },
            );

            if (!response.data.signs || response.data.signs.length === 0) {
                throw new Error('No signs were generated');
            }

            // Backend returns sign data structure formatted JSON
            // Pass it to the callback to update signPlacement
            if (onSignsGenerated) {
                onSignsGenerated(response.data.signs);
            }

            // Close dialog and reset form
            onclose();
            setworkdetails(initial_workdetails);

        } catch (error: any) {
            console.error('Error generating TGS:', error);
            setError(
                error.response?.data?.error || 
                error.message || 
                'Failed to generate TGS. Please try again.'
            );
        } finally {
            setIsLoading(false);
        }
    }

    const handleClose = () => {
        if (!isLoading) {
            setError(null);
            setworkdetails(initial_workdetails);
            onclose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Generate TGS</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <TextField
                    label="Name"
                    value={workdetails.name}
                    onChange={(e) => setworkdetails({...workdetails, name: e.target.value})}
                    fullWidth
                    margin="normal"
                    disabled={isLoading}
                    required
                />
                <TextField
                    label="Email"
                    value={workdetails.email}
                    onChange={(e) => setworkdetails({...workdetails, email: e.target.value})}
                    fullWidth
                    margin="normal"
                    disabled={isLoading}
                    type="email"
                    required
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isLoading}>
                    Cancel
                </Button>
                <Button 
                    onClick={() => handleSubmit(workdetails)} 
                    variant="contained"
                    disabled={isLoading || !workdetails.name || !workdetails.email}
                    startIcon={isLoading ? <CircularProgress size={20} /> : null}
                >
                    {isLoading ? 'Generating...' : 'Generate'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};